import { api } from "@/lib/api";
import { isNetworkError } from "@/lib/offline";

const QKEY = "vt-att-queue";
const CKEY = "vt-att-cache:";
export const QUEUE_EVENT = "vt-att-queue";

const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
const emit = () => window.dispatchEvent(new Event(QUEUE_EVENT));

export const getQueue = () => read(QKEY, []);
export const isOffline = (e) => !navigator.onLine || isNetworkError(e);

// Una sola marca pendiente por alumno/sesión (o por reserva para pagos): la última reemplaza a la anterior.
const keyOf = (it) => (it.type === "paid" ? `paid:${it.reservation_id}` : it.type === "scan" ? `scan:${it.codigo_qr}` : `att:${it.reservation_id}:${it.session_id}`);

export function enqueue(item) {
  const it = { ...item, at: new Date().toISOString() };
  write(QKEY, [...getQueue().filter((q) => keyOf(q) !== keyOf(it)), it]);
  emit();
}

const send = (it) => {
  if (it.type === "manual") return api.post(`/business/attendance/${it.reservation_id}/${it.session_id}/manual`, { present: it.present, marked_at: it.at }, { offlineFallback: true });
  if (it.type === "paid") return api.put(`/business/reservations/${it.reservation_id}/direct-paid`, { paid: it.paid }, { offlineFallback: true });
  return api.post("/business/scan", { codigo_qr: it.codigo_qr, scanned_at: it.at }, { offlineFallback: true });
};

let flushing = null;
export function flushQueue() {
  if (flushing || !navigator.onLine || !getQueue().length) return flushing || Promise.resolve({ sent: 0, failed: [] });
  flushing = (async () => {
    let sent = 0;
    const failed = [];
    for (const it of getQueue()) {
      try {
        const { data } = await send(it);
        if (it.type === "scan" && data && data.valid === false) failed.push({ ...it, reason: data.reason }); else sent += 1;
      } catch (e) {
        if (isNetworkError(e)) break;
        failed.push({ ...it, reason: e.response?.data?.detail });
      }
      write(QKEY, getQueue().filter((q) => keyOf(q) !== keyOf(it)));
    }
    emit();
    return { sent, failed };
  })().finally(() => { flushing = null; });
  return flushing;
}

// GET con copia local: sin señal devuelve la última versión guardada.
export async function cachedGet(url) {
  try {
    const { data } = await api.get(url, { offlineFallback: true });
    write(CKEY + url, { data, at: new Date().toISOString() });
    return { data, offlineAt: null };
  } catch (e) {
    const hit = isNetworkError(e) && read(CKEY + url, null);
    if (hit) return { data: hit.data, offlineAt: hit.at };
    throw e;
  }
}

export const clearAttCache = () => Object.keys(localStorage).filter((k) => k.startsWith(CKEY) || k === QKEY).forEach((k) => localStorage.removeItem(k));

export const manualOpenNow = (s) => {
  const st = new Date(s.starts_at).getTime();
  return s.manual_open || (Date.now() >= st && Date.now() <= st + 48 * 3600 * 1000);
};

// Aplica las marcas pendientes sobre la lista guardada para mostrarlas al instante.
export function overlayAttendance(data) {
  if (!data?.sessions) return data;
  const q = getQueue();
  if (!q.length) return data;
  const sessions = data.sessions.map((s) => {
    const students = s.students.map((x) => {
      const m = q.find((it) => it.type === "manual" && it.reservation_id === x.reservation_id && it.session_id === s.id);
      const p = q.find((it) => it.type === "paid" && it.reservation_id === x.reservation_id);
      let y = x;
      if (m) y = { ...y, attendance: m.present ? "presente" : "ausente", checkin_method: m.present ? "ml" : null, pending_sync: true };
      if (p) y = { ...y, direct_paid: p.paid, pending_sync: true };
      return y;
    });
    const sum = (f) => students.filter(f).reduce((n, x) => n + (x.quantity || 1), 0);
    const owes = (x) => x.direct && !x.direct_paid && x.direct_price_clp > 0;
    return { ...s, students, present: sum((x) => x.attendance === "presente"), absent: sum((x) => x.attendance === "ausente"),
      to_mark: sum((x) => x.attendance === "pendiente"), owing: students.filter(owes).length };
  });
  return { ...data, sessions };
}
